<template>
    <div>
        <div
            v-for="(item, index) in allEditableFilters"
            :key="`${item.data.filter.filter.path}-${index}`">
            <div class="grid-content align-items-start">
                <div class="grid-item grid-item-half">
                    <v-select
                        label="Select Field"
                        :value="item.data.filter.filter.path"
                        :options="filterOptionsList"
                        @input="value => changeFilter(value, item.originalIndex)"/>
                </div>
                <div class="grid-item">
                    <component
                        :is="getComponentType(item.data.filter.filter)"
                        :key="`${item.data.filter.filter.path}-${index}`"
                        v-bind="getComponentProps(item.data.filter)"
                        :inline="true"
                        @update="value => updateFilter(item.originalIndex, value)"
                        @input="value => updateFilter(item.originalIndex, value)">
                        <template slot="footer"><span/></template>
                    </component>
                </div>
                <div class="grid-item no-flex">
                    <span class="field-label">&nbsp;</span>
                    <v-icon-button
                        type="basic"
                        @click="deleteFilter(item.originalIndex)">
                        <v-icon icon="trash"/>
                    </v-icon-button>
                </div>
            </div>
            <div class="divider"></div>
        </div>
        <div
            class="grid-item grid-item-half grid-item-break-half">
            <r-button type="link" @click="addFilter">Add Filters</r-button>
        </div>
    </div>
</template>
<script>
    import Vue from 'vue';
    import FILTER_TYPES from '@/components/query/filter-types';
    import TextFilter from '@/components/query/components/filters/text-filter';
    import BooleanFilter from '@/components/query/components/filters/boolean-filter';
    import EnumFilter from '@/components/query/components/filters/enum-filter';
    import NumericFilter from '@/components/query/components/filters/numeric-filter';
    import SelectFilter from '@/components/query/components/filters/select-filter';
    import DatetimeFilter from '@/components/query/components/filters/datetime-filter';
    import FilterAdapter from '@/components/query/adapters/filter/filter-adapter';
    import {
        vIcon, vIconButton, vSelect,
    } from '@/components/form';
    import labelFromJsonPointer from '@/components/query/helpers/label-from-json-pointer';
    import ActiveFilter from '@/components/query/active-filter';
    import FilterCondition from '@/components/query/spec/filter-condition';

    export default {
        name: 'filters-form',
        components: {
            vSelect,
            vIconButton,
            vIcon,
        },
        props: {
            resource: {
                type: String,
                required: true,
            },
            filter: {
                type: String,
                required: true,
            },
            availableFilters: {
                type: Object,
                required: true,
            },
        },
        data() {
            return {
                adapter: new FilterAdapter(),
                eventBus: new Vue(),
            };
        },
        computed: {
            filterOptionsList() {
                return Object.entries(this.availableFilters).map(([path]) => ({
                    value: path,
                    label: labelFromJsonPointer(path, ' > '),
                }));
            },
            allFilters() {
                return FilterAdapter.convertStringToFilters({
                    filter: this.filter,
                    fields: this.availableFilters,
                });
            },
            allEditableFilters() {
                return this.allFilters
                    .map((data, originalIndex) => ({data, originalIndex}))
                    .filter(item => !!item.data.filter.filter);
            },
        },
        methods: {
            getComponentType({type}) {
                if (type === FILTER_TYPES.text) {
                    return TextFilter;
                } if (type === FILTER_TYPES.boolean) {
                    return BooleanFilter;
                } if (type === FILTER_TYPES.enum) {
                    return EnumFilter;
                } if (type === FILTER_TYPES.numeric) {
                    return NumericFilter;
                } if (type === FILTER_TYPES.select) {
                    return SelectFilter;
                } if (type === FILTER_TYPES.datetime) {
                    return DatetimeFilter;
                }
                // fallback
                return TextFilter;
            },
            getComponentProps(filter) {
                const map = {
                    [FILTER_TYPES.datetime]: {
                        comparison: true,
                        min: null,
                        max: null,
                    },
                    [FILTER_TYPES.select]: {
                        label: 'Select values',
                    },
                };
                return {
                    'event-bus': this.eventBus,
                    adapter: this.adapter,
                    filter,
                    inline: true,
                    ...map[filter.filter.type] ? map[filter.filter.type] : {},
                };
            },
            makeFilter(filters) {
                const query = FilterAdapter.restoreQueryFromFilters(filters);
                if (!query.length) {
                    return null;
                }
                // create new filter string
                return `${FilterAdapter.convertFiltersToString({
                    filterSet: null,
                    query,
                })}`;
            },
            changeFilter(value, index) {
                const filters = this.allFilters.slice(0).map(item => item.filter);
                const filter = new ActiveFilter({
                    filter: {
                        path: value,
                        name: value,
                        ...this.availableFilters[value],
                    },
                });
                filters.splice(index, 1, this.sanitizeInitialFilter(filter));
                this.$emit('update', this.makeFilter(filters));
            },
            deleteFilter(index) {
                const filters = this.allFilters.slice(0).map(item => item.filter);
                filters.splice(index, 1);
                this.$emit('update', this.makeFilter(filters));
            },
            updateFilter(index, {data}) {
                const filters = this.allFilters.slice(0).map(item => item.filter);
                const filter = filters[index];
                filter.condition = new FilterCondition(data);
                this.$emit('update', this.makeFilter(filters));
            },
            addFilter() {
                const filters = this.allFilters.slice(0).map(item => item.filter);
                const availableFilters = Object.entries(this.availableFilters);
                const [name, field] = availableFilters.find(
                    ([fieldName]) => !filters.find(f => f.filter.name === fieldName),
                ) || availableFilters[0];
                const filter = new ActiveFilter({
                    filter: {
                        path: name,
                        name,
                        ...field,
                    },
                });
                filters.push(filter); // add new empty filter
                this.$emit('update', this.makeFilter(filters));
            },
            /**
             * Sanitize initial filter, i.e. if numeric, ensure that max and min have initial values
             * and not just null, else it will build an invalid range filter, i.e. "amount:.." instead
             * of "amount:0..0"
             *
             * @param {ActiveFilter} filter
             * @returns {ActiveFilter}
             */
            sanitizeInitialFilter(filter) {
                switch (filter.filter.type) {
                    case FILTER_TYPES.numeric:
                        if (['max', 'min'].every(key => filter.condition[key] === null)) {
                            filter.condition.min = 0;
                            filter.condition.max = 0;
                        }

                        break;
                    default:
                        break;
                }

                return filter;
            },
        },
    };
</script>
